// src/budget/utils/programBudgetUtils.js

import { MONEY_BLOCKS } from "../../shared/constants/programBudget.js";

/**
 * Collect all distinct fiscal start years across sectioned row arrays.
 *
 * @param {Array<{year?:number}>} approvedRows
 * @param {Array<{year?:number}>} actualRows
 * @param {Array<{year?:number}>} proposedRows
 * @returns {number[]} Sorted list of years.
 */
export function collectAvailableYearsFromRows(
  approvedRows = [],
  actualRows = [],
  proposedRows = []
) {
  const set = new Set();

  approvedRows.forEach((r) => typeof r?.year === "number" && set.add(r.year));
  actualRows.forEach((r) => typeof r?.year === "number" && set.add(r.year));
  proposedRows.forEach((r) => typeof r?.year === "number" && set.add(r.year));

  return [...set].sort((a, b) => a - b);
}

/**
 * Build year availability flags from sectioned row arrays.
 *
 * @param {Array<{year?:number}>} approvedRows
 * @param {Array<{year?:number}>} actualRows
 * @param {Array<{year?:number}>} proposedRows
 * @returns {Map<number, {hasActual:boolean, hasApproved:boolean, hasProposed:boolean}>}
 */
export function buildYearAvailabilityFromRows(
  approvedRows = [],
  actualRows = [],
  proposedRows = []
) {
  const map = new Map();

  const mark = (year, key) => {
    if (typeof year !== "number") return;
    const slot = map.get(year) || {
      hasActual: false,
      hasApproved: false,
      hasProposed: false,
    };
    slot[key] = true;
    map.set(year, slot);
  };

  approvedRows.forEach((r) => mark(r?.year, "hasApproved"));
  actualRows.forEach((r) => mark(r?.year, "hasActual"));
  proposedRows.forEach((r) => mark(r?.year, "hasProposed"));

  return map;
}

/**
 * Convert a year key found in Firestore docs (e.g., "2022") into a safe numeric year.
 *
 * @remarks
 * This function is intentionally strict. It:
 * - Returns `null` for non-finite values (NaN, Infinity).
 * - Returns `null` for years outside [2000, 2100] to avoid treating stray keys
 *   like "fullName" or "metadata" as years.
 *
 * @param {YearKey} yearKey - Candidate year key (e.g., "2022").
 * @returns {number|null}
 * Parsed numeric year, or null if invalid/out-of-range.
 *
 * @example
 * toYearNumber("2022") // 2022
 * toYearNumber("fullName") // null
 */
export function toYearNumber(yearKey) {
  const n = Number(yearKey);
  if (!Number.isFinite(n)) return null;
  if (n < 2000 || n > 2100) return null;
  return n;
}

/**
 * Sum totals for a program section (Approved/Actual/Proposed) across {@link MONEY_BLOCKS}.
 *
 * @remarks
 * This function follows a "canonical total first" approach:
 * 1) If `section.Total` exists and is a finite number, it is returned immediately.
 * 2) Otherwise, the function sums all finite numeric leaf values across each named
 *    money block in {@link MONEY_BLOCKS}.
 *
 * Special handling:
 * - Any leaf key named "Total" (case-insensitive) within a money block is ignored
 *   to prevent double-counting if upstream data included a subtotal line.
 *
 * Return behavior:
 * - Returns `null` if no numeric values were found anywhere in the allowed blocks.
 *   (This is different from returning `0`, and helps distinguish "no data" from
 *   "legitimately zero".)
 *
 * @param {BudgetSection|null|undefined} section
 * Budget section object (Approved/Actual/Proposed) for a specific year.
 * @returns {number|null}
 * Section total dollars, or null if no numeric data exists.
 *
 * @example
 * // Prefers canonical total
 * sumSectionTotals({ Total: 123, Salaries: { Teachers: 999 } }) // 123
 *
 * @example
 * // Computes sum when Total is absent
 * sumSectionTotals({ Salaries: { Teachers: 100 }, Other: { Dues: 5 } }) // 105
 */
export function sumSectionTotals(section) {
  if (!section) return null;

  if (typeof section.Total === "number" && Number.isFinite(section.Total)) {
    return section.Total;
  }

  let total = 0;
  let sawNumber = false;

  MONEY_BLOCKS.forEach((blockName) => {
    const block = section?.[blockName];
    if (!block) return;

    Object.entries(block).forEach(([k, v]) => {
      // Defensive: ignore subtotals embedded in blocks.
      if (/^total$/i.test(String(k))) return;

      if (typeof v === "number" && Number.isFinite(v)) {
        total += v;
        sawNumber = true;
      }
    });
  });

  return sawNumber ? total : null;
}

/**
 * Determine whether a value looks like a numeric-only doc id (e.g., "0200").
 *
 * @remarks
 * Used to decide whether to display docId as part of headings/labels, because
 * some program IDs are numeric (0200, 0300...) while some may not be.
 *
 * @param {unknown} value
 * @returns {boolean}
 * True if value is a string containing only digits after trimming.
 *
 * @example
 * isNumericDocId("0200") // true
 * isNumericDocId("0200 ART") // false
 */
export function isNumericDocId(value) {
  return typeof value === "string" && /^\d+$/.test(value.trim());
}

/**
 * Build the label used by Snapshot-by-Year totals (e.g., `"TOTAL <programLabel>"`).
 *
 * @remarks
 * Rule:
 * - If `docId` is numeric-only, include it (e.g., "0200 ART").
 * - Otherwise, ignore docId and use `fullName` / `programId`.
 *
 * This keeps headings stable when docId is not a clean program code.
 *
 * @param {Object} args
 * @param {string} [args.docId]
 * @param {string} [args.programId]
 * @param {string} [args.fullName]
 * @returns {string}
 * A human-friendly program label suitable for "TOTAL ..." lines.
 */
export function buildSnapshotProgramLabel({ docId, programId, fullName }) {
  const namePart = typeof fullName === "string" ? fullName.trim() : "";

  if (isNumericDocId(docId)) {
    const idPart = docId.trim();
    return `${idPart}${namePart ? ` ${namePart}` : ""}`.trim();
  }

  return (namePart || programId || "").trim();
}

/**
 * Build the display heading used in UI sections (snapshot title, chart titles, etc.).
 *
 * @remarks
 * Rule:
 * - If docId is numeric-only, show `"<docId> <fullName>"`.
 * - Otherwise, show `<fullName>` (fallback to `programId`).
 *
 * @param {Object} args
 * @param {string} [args.docId]
 * @param {string} [args.programId]
 * @param {string} [args.fullName]
 * @returns {string}
 * Display heading string.
 */
export function buildDisplayHeading({ docId, programId, fullName }) {
  const name = (typeof fullName === "string" && fullName.trim()) || programId;
  return isNumericDocId(docId) ? `${docId.trim()} ${name}` : name;
}

/**
 * Remove snapshot subsections that contain no non-zero numeric values.
 *
 * @remarks
 * The Snapshot-by-Year table is structured as:
 * - A "section header" row with `{ section: "Some Section" }`
 * - Followed by one or more detail rows
 *
 * This function removes the entire subsection (header + detail rows) when the
 * detail rows contain no non-zero numeric values across the numeric columns.
 *
 * Important semantics:
 * - "Non-zero" means `v !== 0`. Both +10 and -10 are considered non-zero.
 * - Rows whose label starts with "TOTAL" (case-insensitive) are *never* removed
 *   and cause any pending subsection to flush first.
 * - Numeric columns are derived from `columns[].key`, excluding:
 *   "label", "year", and "section".
 *
 * @param {SnapshotRow[]|null|undefined} rows
 * Snapshot table rows (including section headers and totals).
 * @param {ColumnDef[]|null|undefined} columns
 * Snapshot table columns used to determine numeric fields.
 * @returns {SnapshotRow[]}
 * A filtered row array with zero-only subsections removed.
 */
export function filterZeroSnapshotSections(rows, columns) {
  if (!Array.isArray(rows) || !Array.isArray(columns)) return rows || [];

  const numericKeys = columns
    .map((c) => c?.key)
    .filter((k) => k && k !== "label" && k !== "year" && k !== "section");

  /**
   * @param {SnapshotRow[]} rowList
   * @returns {boolean} True if any numeric key contains a finite non-zero number.
   */
  const hasAnyNonZeroNumeric = (rowList) =>
    rowList.some((r) =>
      numericKeys.some((k) => {
        const v = r?.[k];
        return typeof v === "number" && Number.isFinite(v) && v !== 0;
      })
    );

  const out = [];
  let pendingHeader = null;
  let pendingRows = [];

  const flush = () => {
    if (!pendingHeader) {
      out.push(...pendingRows);
    } else if (hasAnyNonZeroNumeric(pendingRows)) {
      out.push(pendingHeader, ...pendingRows);
    }
    pendingHeader = null;
    pendingRows = [];
  };

  for (const row of rows) {
    // Section header row
    if (row && typeof row.section === "string" && row.section.trim() !== "") {
      flush();
      pendingHeader = row;
      continue;
    }

    // Grand total rows should always remain, and they terminate any pending section.
    const label = typeof row?.label === "string" ? row.label.toUpperCase() : "";
    const isGrandTotalRow = label.startsWith("TOTAL");
    if (isGrandTotalRow) {
      flush();
      out.push(row);
      continue;
    }

    // Normal detail row
    if (pendingHeader) pendingRows.push(row);
    else out.push(row);
  }

  flush();
  return out;
}

/**
 * Build Approved-table rows, injecting a Proposed fallback row when needed.
 *
 * @remarks
 * This mirrors the UI behavior in ProgramBudget where the Approved table should
 * still show "the latest year" even if Approved is missing but Proposed exists.
 *
 * Rules:
 * - Identify the latest year present in `proposedRows`.
 * - If Approved already includes that year, return Approved unchanged.
 * - Otherwise, append the Proposed row for that year to the Approved list.
 *
 * Output row annotation:
 * - Adds `isProposedFallback: true`
 * - Adds `rowClassName: "bg-yellow-100"` for visual highlighting
 * - Ensures `sortYear` exists (for stable ordering)
 *
 * IMPORTANT:
 * - This is intended ONLY for Approved *table rendering*.
 * - Charts should continue to use real Approved series (no Proposed substitution),
 *   unless explicitly designed otherwise.
 *
 * @param {Array<{year?:number, sortYear?:number}>|null|undefined} approvedRows
 * @param {Array<{year?:number, sortYear?:number}>|null|undefined} proposedRows
 * @returns {Array<any>}
 * Approved rows, possibly with one extra Proposed fallback row appended.
 */
export function buildApprovedRowsForTable(approvedRows, proposedRows) {
  if (!Array.isArray(approvedRows) || !Array.isArray(proposedRows)) {
    return approvedRows || [];
  }

  const proposedYears = proposedRows
    .map((r) => r?.year)
    .filter((y) => typeof y === "number");

  if (proposedYears.length === 0) return approvedRows;

  const latestProposedYear = Math.max(...proposedYears);

  const approvedHasLatest = approvedRows.some(
    (r) => r?.year === latestProposedYear
  );
  if (approvedHasLatest) return approvedRows;

  const proposedRow = proposedRows.find((r) => r?.year === latestProposedYear);
  if (!proposedRow) return approvedRows;

  return [
    ...approvedRows,
    {
      ...proposedRow,
      sortYear: proposedRow.sortYear ?? proposedRow.year ?? latestProposedYear,
      isProposedFallback: true,
      rowClassName: "bg-yellow-100",
    },
  ];
}

/**
 * Determine whether a section "has data" based on non-zero money values.
 *
 * @remarks
 * A section is considered present/available if it contains at least one finite,
 * non-zero numeric value within any of the {@link MONEY_BLOCKS}.
 *
 * This is stronger than "has keys" and avoids treating blank templates or
 * all-zero sections as available.
 *
 * @param {BudgetSection|null|undefined} section
 * Budget section object (Approved/Actual/Proposed).
 * @returns {boolean}
 * True if any money value is numeric, finite, and non-zero.
 */
export function hasAnyNonZeroMoney(section) {
  if (!section) return false;

  return MONEY_BLOCKS.some((blockName) => {
    const block = section?.[blockName];
    if (!block) return false;

    return Object.values(block).some(
      (v) => typeof v === "number" && Number.isFinite(v) && v !== 0
    );
  });
}

/**
 * Get an "Approved-like" total for a year: Approved total if present, else Proposed.
 *
 * @remarks
 * This mirrors the "Approved table shows Proposed at end if Approved is missing"
 * behavior in ProgramBudget, but at the per-program/per-year level.
 *
 * It is commonly used for:
 * - Total approved-by-year charts that should include proposed-only years.
 * - Snapshot tables where ending-year Approved column should show proposed if
 *   Approved is absent (normal mode behavior).
 *
 * @param {ProgramYearObject|null|undefined} yearObj
 * The object stored at `progDoc[yearKey]`.
 * @returns {number|null}
 * Approved total if available; otherwise Proposed total; otherwise null.
 */
export function getApprovedTotalWithProposedFallback(yearObj) {
  if (!yearObj) return null;

  const approvedTotal = sumSectionTotals(yearObj?.Approved);
  if (typeof approvedTotal === "number" && Number.isFinite(approvedTotal)) {
    return approvedTotal;
  }

  const proposedTotal = sumSectionTotals(yearObj?.Proposed);
  if (typeof proposedTotal === "number" && Number.isFinite(proposedTotal)) {
    return proposedTotal;
  }

  return null;
}

/**
 * Extract the Firestore document ID from a program key string.
 *
 * @remarks
 * Program config keys often look like `"0200 ART"` or
 * `"0300 BASIC EDUCATION ( K - 6 )"`. The doc id is the first token.
 *
 * @param {string} programKey - Program key string.
 * @returns {string}
 * The Firestore doc id (first whitespace-delimited token).
 *
 * @example
 * getDocIdFromProgramKey("0200 ART") // "0200"
 */
export function getDocIdFromProgramKey(programKey) {
  return String(programKey).split(" ")[0];
}

/**
 * Build availability flags across years for Actual/Approved/Proposed, aggregated
 * across *all* program docs provided.
 *
 * @remarks
 * A year is considered available for a section if **any** program has at least one
 * non-zero money value in that section for that year.
 *
 * This is used to:
 * - Decide which years are valid ending-year options for snapshot windows
 * - Decide which years can be charted for Approved/Actual totals
 * - Drive UI selectors for available year/type combinations
 *
 * @param {Record<string, ProgramDoc>|null|undefined} programDocs
 * Map of program docId -> Firestore doc data.
 *
 * @returns {Map<number, YearAvailability>}
 * A Map keyed by numeric fiscal-start year:
 * `year -> { hasActual, hasApproved, hasProposed }`
 *
 * @example
 * const avail = buildYearAvailability({ "0200": doc0200Data, "0300": doc0300Data });
 * avail.get(2024)?.hasProposed // true/false
 */
export function buildYearAvailability(programDocs) {
  /** @type {Map<number, YearAvailability>} */
  const byYear = new Map();

  /**
   * Ensure the availability object exists for a year.
   * @param {number} y
   * @returns {YearAvailability}
   */
  const ensure = (y) => {
    if (!byYear.has(y)) {
      byYear.set(y, {
        hasActual: false,
        hasApproved: false,
        hasProposed: false,
      });
    }
    return byYear.get(y);
  };

  Object.values(programDocs || {}).forEach((docData) => {
    if (!docData || typeof docData !== "object") return;

    Object.keys(docData).forEach((yearKey) => {
      const y = toYearNumber(yearKey);
      if (!y) return;

      const yearObj = docData[yearKey] || {};
      const slot = ensure(y);

      if (hasAnyNonZeroMoney(yearObj?.Actual)) slot.hasActual = true;
      if (hasAnyNonZeroMoney(yearObj?.Approved)) slot.hasApproved = true;
      if (hasAnyNonZeroMoney(yearObj?.Proposed)) slot.hasProposed = true;
    });
  });

  return byYear;
}

export function getValidWindowEndingYears(years, yearAvail) {
  const hasActual = (y) => yearAvail.get(y)?.hasActual === true;
  const hasApproved = (y) => yearAvail.get(y)?.hasApproved === true;
  const hasProposed = (y) => yearAvail.get(y)?.hasProposed === true;

  // Normal: ... Actual(Y-2), Approved(Y-1), Actual(Y-1), Approved/Proposed(Y)
  const isValidNormal = (y) =>
    hasActual(y - 2) &&
    hasApproved(y - 1) &&
    hasActual(y - 1) &&
    (hasApproved(y) || hasProposed(y));

  // Proposed-only: ... Actual(Y-3), Actual(Y-2), Approved(Y-1), Proposed(Y)
  const isValidProposedOnly = (y) =>
    hasProposed(y) &&
    !hasApproved(y) &&
    hasActual(y - 3) &&
    hasActual(y - 2) &&
    hasApproved(y - 1);

  // Forward Approved-only:
  // When current year is Approved but prior-year Actual doesn't exist yet,
  // show ... Actual(Y-3), Actual(Y-2), Approved(Y-1), Approved(Y)
  const isValidForwardApprovedOnly = (y) =>
    hasApproved(y) &&
    !hasProposed(y) &&
    hasActual(y - 3) &&
    hasActual(y - 2) &&
    hasApproved(y - 1) &&
    !hasActual(y - 1);

  return years
    .filter((y) => isValidNormal(y) || isValidProposedOnly(y) || isValidForwardApprovedOnly(y))
    .sort((a, b) => a - b);
}

export function isProposedOnlyMode(endingYear, yearAvail) {
  const slot = yearAvail.get(endingYear);
  return slot?.hasProposed === true && slot?.hasApproved !== true;
}

export function isForwardApprovedOnlyMode(endingYear, yearAvail) {
  const hasActual = (y) => yearAvail.get(y)?.hasActual === true;
  const hasApproved = (y) => yearAvail.get(y)?.hasApproved === true;
  const hasProposed = (y) => yearAvail.get(y)?.hasProposed === true;

  return (
    hasApproved(endingYear) &&
    !hasProposed(endingYear) &&
    hasActual(endingYear - 3) &&
    hasActual(endingYear - 2) &&
    hasApproved(endingYear - 1) &&
    !hasActual(endingYear - 1)
  );
}

export function build4ColWindowMeta(endingYear, proposedOnly, forwardApprovedOnly = false) {
  if (!endingYear) return [];

  if (proposedOnly) {
    return [
      { year: endingYear - 3, sectionKey: "Actual",   colKey: `actual_${endingYear - 3}` },
      { year: endingYear - 2, sectionKey: "Actual",   colKey: `actual_${endingYear - 2}` },
      { year: endingYear - 1, sectionKey: "Approved", colKey: `approved_${endingYear - 1}` },
      { year: endingYear,     sectionKey: "Proposed", colKey: `proposed_${endingYear}` },
    ];
  }

  if (forwardApprovedOnly) {
    return [
      { year: endingYear - 3, sectionKey: "Actual",   colKey: `actual_${endingYear - 3}` },
      { year: endingYear - 2, sectionKey: "Actual",   colKey: `actual_${endingYear - 2}` },
      { year: endingYear - 1, sectionKey: "Approved", colKey: `approved_${endingYear - 1}` },
      { year: endingYear,     sectionKey: "Approved", colKey: `approved_${endingYear}` },
    ];
  }

  return [
    { year: endingYear - 2, sectionKey: "Actual",   colKey: `actual_${endingYear - 2}` },
    { year: endingYear - 1, sectionKey: "Approved", colKey: `approved_${endingYear - 1}` },
    { year: endingYear - 1, sectionKey: "Actual",   colKey: `actual_${endingYear - 1}` },
    { year: endingYear,     sectionKey: "Approved", colKey: `approved_${endingYear}` },
  ];
}

